import React from "react";

const IdentityValueVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson explores values as the hidden driver behind daily choices and career direction. It frames values as non negotiables that reduce drift and overthinking, and as a filter for what you say yes to and what you refuse. It also highlights a second layer: you work inside a system where stakeholder values shape labels like dependable or difficult. When values align, trust compounds and growth becomes easier.
      </p>
    </div>
  );

  const transcriptHtml = `
    <p>Every choice you make, every decision you take, stems from something deeper than the task itself. It comes from your <strong>values</strong>, your internal compass. In your career, and life, values are the foundation. They shape the person you are, the work you do, and ultimately, the promotions you earn.</p>

    <p>Every day, you make thousands of decisions, some estimates suggest <strong>~35,000</strong>. Most of these choices are small, almost invisible, yet cumulatively, they shape your trajectory. Without a clear sense of what truly matters to you, you either drift, overthink, or react impulsively.</p>

    <p><strong>Values are your short list of non-negotiables.</strong> They answer a single question: What matters most when I have to choose?</p>

    <p>They are your filter. Your brake. Your guide.</p>

    <p>Consider this: two options sit before you, both appealing. Which do you choose? Values provide clarity, letting you decide with confidence and without regret. They influence daily decisions, speak up or stay quiet, cut corners or uphold quality, and long-term ones, selecting projects, teams, and opportunities that align with who you are.</p>

    <p>But here's the thing most people miss, your values don't exist in isolation.</p>

    <p>You operate inside a system, with stakeholders whose values steer their decisions too.</p>

    <p>If your core values clash with your stakeholder's, say, you value autonomy, they value control, <strong>friction is inevitable.</strong></p>

    <p>If they align, that is, you both value ownership and results, <strong>trust compounds fast</strong>, and growth becomes natural.</p>

    <p>So, clarity isn't just knowing your values. It's knowing how they sync with theirs.</p>

    <p>That alignment determines whether you're seen as "difficult" or "dependable,"</p>
    <p>as "resistant" or "ready for more."</p>

    <p><em>Visual break here, add illustrations or stock videos</em></p>

    <p><strong>High achievers are not just faster, they are clearer.</strong> They know their top three to five values. This clarity becomes their strategic advantage. They are able to:</p>

    <ul style="margin-left: 20px;">
      <li><strong>Filter quickly</strong></li>
      <li><strong>Say no decisively</strong></li>
      <li><strong>Stay consistent</strong></li>
    </ul>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Filter quickly:</h3>
    <p>If 'Impact' and 'Ownership' are core, they pursue work with measurable outcomes where their contribution matters.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Say no decisively:</h3>
    <p>If a task violates their values, they decline or reframe it rather than drift. If a person values autonomy, they may avoid daily micromanaged meetings, proposing outcome-based plans instead.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Stay consistent:</h3>
    <p>Choices are steady, not mood-driven. Consistency compounds trust. Someone valuing security may resist a flashy end-of-quarter launch, focusing instead on reliability and risk mitigation. Over time, this builds a reputation for reliability and judgment.</p>

    <p>Your values are the lens through which all opportunities are filtered. They determine where your energy goes, the decisions you make, and ultimately, how you are seen. Choosing wisely today compounds into trust, visibility, and opportunity tomorrow.</p>

    <p><strong>Reflect on your own core values. Which values define your career?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default IdentityValueVideo;